import { Anchor, Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { IconDownload, IconExternalLink } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { saveBlob } from '@feature/utils';

import {
  getTravelReferenceDocumentContentOptions,
  useTravelReferenceDocumentsRefresh,
} from '../model/TravelReferenceDocumentQuery';
import {
  TravelReferenceDocumentContentSource,
  TravelReferenceDocumentFile,
} from '../model/TravelReferenceDocumentTypes';

interface TravelReferenceDocumentViewerProps {
  document: TravelReferenceDocumentFile;
  source?: TravelReferenceDocumentContentSource;
  title?: string;
}

export function TravelReferenceDocumentViewer(props: TravelReferenceDocumentViewerProps) {
  const { document, source = 'resolved' } = props;
  const { t } = useTranslation();
  const { data, isError } = useQuery(getTravelReferenceDocumentContentOptions(document, source));
  const refreshDocuments = useTravelReferenceDocumentsRefresh();
  const [url, setUrl] = useState<string | null>(null);
  const title = props.title ?? t(`travel.referenceDocuments.kindEnum.${document.kind}`);

  useEffect(() => {
    if (data == null) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = window.URL.createObjectURL(data);
    setUrl(objectUrl);

    return () => window.URL.revokeObjectURL(objectUrl);
  }, [data]);

  // The list can still offer a document whose file is gone, and only a fresh list drops it.
  useEffect(() => {
    if (isError) void refreshDocuments();
  }, [isError, refreshDocuments]);

  if (isError) {
    return <Text c="red">{t('travel.referenceDocuments.loadFailed')}</Text>;
  }

  if (data == null || url == null) {
    return (
      <Center h={200}>
        <Loader
          role="status"
          aria-label={t('travel.referenceDocuments.loading')}
        />
      </Center>
    );
  }

  return (
    <Stack gap="xs">
      <Group gap="md">
        <Anchor
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
        >
          <Group
            gap={4}
            wrap="nowrap"
            component="span"
          >
            <IconExternalLink size={14} />
            {t('travel.referenceDocuments.openInNewTab')}
          </Group>
        </Anchor>
        <Button
          size="xs"
          variant="outline"
          leftSection={<IconDownload size={14} />}
          onClick={() => saveBlob(data, document.fileName)}
        >
          {t('button.download')}
        </Button>
      </Group>
      <iframe
        src={url}
        title={title}
        style={{ display: 'block', width: '100%', height: '80vh', minHeight: 420, border: 0 }}
      />
    </Stack>
  );
}
